import {
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery,
} from '@apollo/client'
import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import {
    Admin,
    PublishingAuthor,
} from '@deltek/specification-client/core/Configurations/Authorization'
import {
    GetDesignatedAdminRoutingComponentProps,
    getDesignatedAdminRoutingComponent,
} from '@deltek/specification-client/core/Configurations/DesignatedAdminRouting'
import {
    GlobalSnackbar,
    GlobalSnackbarPropsQuery,
    GlobalSnackbarPropsQueryResponse,
    HideGlobalSnackbarMutation,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import ContentContainer from '@deltek/specification-client/core/Containers/ContentContainer'
import { AppHeaderContext } from '@deltek/specification-client/core/Contexts/AppHeaderContext'
import {
    ILoadingPageContext,
    LoadingPageContext,
} from '@deltek/specification-client/core/Contexts/LoadingPageContext'
import {
    CurrentUserActiveFirmQuery,
    GetReleaseNoteUriQuery,
} from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import LowerToolbar from '@deltek/specification-client/core/Headers/LowerToolbar'
import TopBarTools from '@deltek/specification-client/core/Headers/TopBarTools'
import ResponsiveMenus from '@deltek/specification-client/core/Menus/ResponsiveMenus'
import Sidebar from '@deltek/specification-client/core/Menus/Sidebar'
import {
    CurrentUserActiveFirmResponse,
    CurrentUserFirmsProps,
    MasterSpecNoteUriResponse,
} from '@deltek/specification-client/core/Models/currentUserInfo'
import { FirmId } from '@deltek/specification-client/core/Models/firm'
import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import { FirmUserResponse } from '@deltek/specification-client/core/Models/user'
import TermsAndConditions from '@deltek/specification-client/core/Pages/TermsAndConditions'
import DoubleAppToolbar from '@deltek/specification-client/core/Surfaces/DoubleAppToolbar'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import { isDeltekUserorAdmin } from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import { Theme, ThemeProvider, makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import {
    AcceptTermsAndConditionsMutation,
    AddUserTokenToBlacklistMutation,
} from 'api/graphQL/mutations/user.mutations'
import { UserTAndCQuery } from 'api/graphQL/queries/user.queries'
import { useIsBPMReseller } from 'bpm/api/graphQL/hooks/useIsBPMReseller'
import {
    FirmDesignatedAccountAdminQuery,
    FirmSupportContactUserQuery,
    GetFirmGainsightAggregatesQuery,
} from 'bpm/api/graphQL/queries/firm.queries'
import { UserProfileQuery } from 'bpm/api/graphQL/queries/userProfile.queries'
import {
    FirmGainsightAggregatesResponse,
    FirmResponse,
} from 'bpm/api/graphQL/types/firm.types'
import UserProfileUpdate from 'bpm/components/UserProfileUpdate'
import MainPageRouting from 'bpm/pages/MainPage/MainPageRouting'
import { isUserBPMAdmin } from 'bpm/pages/ProductsPage/productsUtils'
import AppHeader from 'components/AppHeader/AppHeader'
import ContextPill from 'components/AppHeader/ContextPill'
import HarmonyHelpButton from 'components/AppHeader/HarmonyHelpButton'
import HarmonyNotificationBell from 'components/AppHeader/HarmonyNotificationBell'
import {
    isProduction,
    isStaging,
    processEnv,
} from 'components/configuration/environmentVariables'
import { harmonyTheme } from 'components/configuration/harmonyTheme'
import React, {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { BPMRoutes } from 'routes/bpmRoutes'
import useIsHarmonyHeader from 'shared/hooks/useIsHarmonyHeader'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import AuthEventService from 'shared/services/authEventService'
import { GAINSIGHT_APP_BPM } from 'shared/utils/gainsight'
import { useGainsightIdentify } from 'shared/utils/useGainsightIdentify'
import {
    getFromLocalStorage,
    updatePersistentConfigs,
} from 'shared/utils/workspaceUtils'
import {
    generateOnlineHelpUrl,
    getHelpDeskEmailAddress,
    isUserDesignatedAccountAdmin,
    useSidebarItems,
} from './mainPageUtils'

const bpmReleaseNotesUrl =
    'https://dsm.deltek.com/DeltekSoftwareManagerWebServices/downloadFile.ashx?documentid=005B92D0-4360-42CD-A97B-1E7F3CA008F1'

const HARMONY_HEADER_HEIGHT = 110
const HARMONY_HEADER_HEIGHT_WITH_TOOLBAR = 160
const CLASSIC_HEADER_HEIGHT = 100
const CLASSIC_HEADER_HEIGHT_WITH_TOOLBAR = 150
const BPM_GREY_700 = '#5E5F61'
const ANALYTICS_BG_COLOR = '#E2E4E9'

const ANALYTICS_HOME_PATH = '/bpm/analytics/home'

const useStyles = makeStyles((theme: Theme) => ({
    harmonyLowerWrapper: {
        boxShadow: theme.shadows[4],
        position: 'relative',
        zIndex: theme.zIndex.appBar,
    },
    analyticsContentContainer: {
        backgroundColor: ANALYTICS_BG_COLOR,
    },
}))

// AppHeader and everything it renders (NavBarMenu, ContextPill, HarmonyHelpButton, etc.)
const harmonyHeaderTheme = (outerTheme: Theme): Theme => ({
    ...outerTheme,
    palette: {
        ...outerTheme.palette,
        grey: {
            ...outerTheme.palette.grey,
            700: BPM_GREY_700,
        },
    },
    harmony: harmonyTheme,
})

const MainPageBPM: React.FC = () => {
    const client = useApolloClient()
    const location = useLocation()
    const history = useHistory()
    const theme = useTheme()
    const isHarmonyHeader = useIsHarmonyHeader()
    const { loading: previewFeaturesLoading, previewFeatures } = usePreviewFeatures()
    const classes = useStyles()

    const currentUserInfoResponse = useContext(AuthContext)
    const { logOut, changePassword, currentUserInfo } = currentUserInfoResponse

    const { data: currentUserData } = useQuery<FirmUserResponse>(
        UserProfileQuery,
        {
            variables: {
                firmId: currentUserInfo?.account?.accountId ?? '',
                userId: currentUserInfo?.kpUserId ?? '',
            },
            skip: !currentUserInfo,
        }
    )

    const { data: masterSpecReleaseNoteUri } = useQuery<
        MasterSpecNoteUriResponse
    >(GetReleaseNoteUriQuery, {
        fetchPolicy: "cache-first",
        variables: {
            input: {
                releaseNoteName: 'MASTERSPEC',
            },
        },
    })

    const userRole = React.useMemo(() => currentUserInfo?.roles[0] ?? '', [
        currentUserInfo,
    ])

    const {
        data: firmSupportContactData,
        loading: firmSupportContactLoading,
    } = useQuery<FirmResponse, FirmId>(FirmSupportContactUserQuery, {
        variables: {
            firmId: currentUserInfo?.account?.accountId ?? '',
        },
        skip: !currentUserInfo,
    })

    const { data: firmGainsightAggregatesData } = useQuery<
        FirmGainsightAggregatesResponse,
        FirmId
    >(GetFirmGainsightAggregatesQuery, {
        variables: {
            firmId: currentUserInfo?.account?.accountId ?? '',
        },
        skip: !currentUserInfo,
    })

    useGainsightIdentify({
        currentUserInfo,
        firmSupportContactData,
        firmGainsightAggregatesData,
        currentUserData,
        app: GAINSIGHT_APP_BPM,
    })

    const { data: globalSnackbarPropsData } = useQuery<
        GlobalSnackbarPropsQueryResponse
    >(GlobalSnackbarPropsQuery)
    const [
        getFirmDesignatedAdminUserId,
        {
            called: firmDesignatedAdminCalled,
            loading: firmDesignatedAdminLoading,
            data: firmDesignatedAdminResponse,
        },
    ] = useLazyQuery<FirmResponse>(FirmDesignatedAccountAdminQuery, {
        fetchPolicy: 'no-cache',
    })

    const contentContainerRef = useRef<HTMLDivElement>(null)
    const [hideGlobalSnackbar] = useMutation(HideGlobalSnackbarMutation)
    const [addUserTokenToBlacklist] = useMutation(
        AddUserTokenToBlacklistMutation
    )

    let smScreen = useMediaQuery(theme.breakpoints.up('sm'))
    const [sidebarOpen, setSidebarOpen] = React.useState(!smScreen)
    const [userProfileClicked, setUserProfileClicked] = React.useState<boolean>(
        false
    )

    const [userFirmDetail, setUserFirmDetail] = React.useState<
        CurrentUserFirmsProps
    >()

    const {
        customComponent,
        pageTitle,
        customTextColor,
        barColor,
        hasLowerBottomToolbar,
        setHasLowerBottomToolbar,
        fullHeight,
        isCustomToolbar,
    } = React.useContext(AppHeaderContext)

    const toggleSidebarOpen = React.useCallback(
        (newValue: boolean | 'toggle') => {
            if (newValue !== 'toggle') setSidebarOpen(newValue)
            else setSidebarOpen(!sidebarOpen)
        },
        [sidebarOpen]
    )
    const onUserProfileClick = useCallback(() => {
        setUserProfileClicked(true)
    }, [])

    const onFirmSelect = useCallback(() => {
        // BPM never calls setUser(), so the userInfo reactive var is empty here.
        // Read kpUserId from AuthContext or the toggle restore below silently fails.
        const userId = currentUserInfo?.kpUserId
        const betaToggle = getFromLocalStorage(
            userId ?? '',
            'Global',
            'betaWorkspaceToggleChecked'
        )

        // Emit logout event to notify services (e.g., clear preview features cache)
        AuthEventService.emitLogout()

        window.localStorage.clear()

        if (betaToggle !== null) {
            updatePersistentConfigs(
                userId ?? '',
                'Global',
                'betaWorkspaceToggleChecked',
                betaToggle
            )
        }
        window.open(userFirmDetail?.firmAccountTypeApplicationURL, '_self')
    }, [userFirmDetail, currentUserInfo])

    const getUserFirm = useCallback((data: CurrentUserFirmsProps) => {
        setUserFirmDetail(data)
    }, [])

    const handleHamburgerClick = useCallback(() => toggleSidebarOpen('toggle'), [toggleSidebarOpen])

    const helpDeskEmailAddress: string = React.useMemo(() => {
        if (!firmSupportContactLoading && firmSupportContactData) {
            return getHelpDeskEmailAddress(
                currentUserInfo!,
                firmSupportContactData?.firm
            )
        }
        return ''
    }, [firmSupportContactLoading, firmSupportContactData, currentUserInfo])

    const userIsDesignatedSupportPerson: boolean = React.useMemo(() => {
        return isUserDesignatedAccountAdmin(
            currentUserInfo!,
            firmSupportContactData?.firm!
        )
    }, [currentUserInfo, firmSupportContactData])

    const hideIndustryAnalytics: boolean = React.useMemo(() => {
        return !firmSupportContactLoading && firmSupportContactData
            ? firmSupportContactData?.firm?.hideIndustryAnalytics ?? false
            : false
    }, [firmSupportContactLoading, firmSupportContactData])

    const { data: currentUserActiveFirmResponse } = useQuery<
        CurrentUserActiveFirmResponse
    >(CurrentUserActiveFirmQuery)

    const onlineHelpUrl: string = React.useMemo(() => {
        let userAccountType = currentUserInfo?.account.accountType ?? ''
        if (
            userAccountType === 'System' &&
            currentUserActiveFirmResponse?.myActiveFirm.firmAccountType ===
            'AEFirm'
        ) {
            userAccountType = 'AEFirm'
        }
        return generateOnlineHelpUrl(userAccountType)
    }, [currentUserActiveFirmResponse, currentUserInfo, generateOnlineHelpUrl])

    const {
        isDeltekUser,
        isDeltekAdmin,
        isDeltekAuthor,
        isDeltekReviewer,
    } = isDeltekUserorAdmin(currentUserInfoResponse)

    const isPublishingAuthor = React.useMemo(
        () => currentUserInfo?.roles.includes(PublishingAuthor) ?? false,
        [currentUserInfoResponse]
    )

    const isBPM = React.useMemo(
        () => !!isUserBPMAdmin(currentUserInfoResponse).isBPM,
        [currentUserInfoResponse]
    )

    const isBPMReseller = useIsBPMReseller()
    const isOnboarder = currentUserData?.user?.isOnboarder ?? false

    const sidebarItems = useSidebarItems({
        userRole,
        isBPM,
        isDeltekUser,
        isDeltekAdmin,
        isDeltekAuthor,
        isPublishingAuthor,
        isDeltekReviewer,
        hideIndustryAnalytics,
        hasCompanyAccountAccess: isOnboarder,
        isBPMReseller,
        hasMatchScorePage: previewFeatures.hasMatchScorePage,
    })

    const helpMenuItems: MenuItemConfig[] = React.useMemo(
        () => [
            {
                dataTestId: 'globalHelpMenuItemOnlineHelp',
                label: 'Online Help',
                onClick: () => navigateToExternalLink?.(onlineHelpUrl),
            },
            {
                dataTestId: 'globalHelpMenuItemReleaseNotes',
                label: 'Release Notes',
                onClick: () => navigateToExternalLink?.(bpmReleaseNotesUrl),
            },
            {
                dataTestId: 'globalHelpMenuItemEmailHelpDesk',
                label: 'Email Help Desk',
                onClick: () =>
                    navigateToExternalLink?.(
                        `mailto:${helpDeskEmailAddress}`,
                        true
                    ),
            },
            ...(userIsDesignatedSupportPerson
                ? [
                    {
                        dataTestId: 'globalHelpMenuItemDesktopSupportCenter',
                        label: 'Deltek Support Center',
                        onClick: () =>
                            navigateToExternalLink?.(
                                'https://deltek.custhelp.com'
                            ),
                    },
                ]
                : []),
            {
                label: 'Privacy Policy',
                dataTestId: 'globalHelpMenuItemPrivacyPolicy',
                onClick: () =>
                    navigateToExternalLink?.(
                        'https://www.deltek.com/en/deltek-contracts/is-user-privacy-policy'
                    ),
            },
            {
                label: 'Terms of Use',
                dataTestId: 'globalHelpMenuItemTermsOfUse',
                onClick: () =>
                    navigateToExternalLink?.(
                        'https://www.deltek.com/en/deltek-contracts/masterspec'
                    ),
            },
        ],
        [
            helpDeskEmailAddress,
            userIsDesignatedSupportPerson,
            onlineHelpUrl,
            masterSpecReleaseNoteUri,
        ]
    )

    const handleLogout = React.useCallback(async () => {
        // Emit logout event to notify services (e.g., clear preview features cache)
        // This must happen BEFORE logout to ensure all listeners can clean up
        AuthEventService.emitLogout()

        await addUserTokenToBlacklist()
        logOut()
    }, [logOut, currentUserInfoResponse, addUserTokenToBlacklist])

    const responsiveMenus = React.useMemo(() => (
        <ResponsiveMenus
            accountType={currentUserInfo?.account.accountType}
            showProductionOnlineHelp={isProduction || isStaging}
            onUserProfileClick={onUserProfileClick}
            userProfileImage={currentUserData?.user?.profileInfo?.imageUrl}
            changePassword={changePassword}
            logout={handleLogout}
            userName={{
                first: currentUserInfo?.firstName,
                last: currentUserInfo?.lastName,
                userId: currentUserInfo?.kpUserId,
            }}
            onFirmSelect={onFirmSelect}
            showMultiFirm={!isHarmonyHeader}
            buildNumber={processEnv.REACT_APP_BuildNumber}
            helpMenuItems={helpMenuItems}
            releaseNoteUrl={masterSpecReleaseNoteUri?.getReleaseNoteUri}
            getUserFirms={getUserFirm}
            pollInterval={parseInt(processEnv.REACT_APP_NotifInterval)}
        />
    ), [
        helpMenuItems,
        handleLogout,
        currentUserInfo,
        changePassword,
        currentUserData,
        onUserProfileClick,
        onFirmSelect,
        isHarmonyHeader,
        masterSpecReleaseNoteUri,
        getUserFirm,
    ])

    const harmonySideContent = React.useMemo(() => (
        <>
            <HarmonyHelpButton
                helpMenuItems={helpMenuItems}
                buildNumber={processEnv.REACT_APP_BuildNumber}
            />
            <HarmonyNotificationBell />
            {responsiveMenus}
            <ContextPill
                getUserFirms={getUserFirm}
                onFirmSelect={onFirmSelect}
            />
        </>
    ), [helpMenuItems, responsiveMenus, getUserFirm, onFirmSelect])

    const { handleLoadingPageChange, showLoadingScreenPage } = useContext<
        ILoadingPageContext
    >(LoadingPageContext)
    const [isTermsAccepted, setIsTermsAccepted] = React.useState<
        boolean | undefined
    >()

    const { data: userTandCDate, loading: userTandCDateLoading } = useQuery(
        UserTAndCQuery,
        {
            onCompleted: () => {
                if (
                    userTandCDate &&
                    userTandCDate.myTermsAcceptanceDate !== ''
                ) {
                    setIsTermsAccepted(true)
                }
            },
        }
    )

    const [acceptTermsAndConditions] = useMutation(
        AcceptTermsAndConditionsMutation
    )

    const handleDialogClose = () => {
        const searchParams = new URLSearchParams(location.search)
        if (searchParams.has('newuser')) {
            searchParams.delete('newuser')
            history.replace({
                ...location,
                search: `?${searchParams.toString()}`,
            })
        }
        setUserProfileClicked(false)
    }

    const handleTermsDialogDisplay = React.useCallback(() => {
        setIsTermsAccepted(!isTermsAccepted)
        acceptTermsAndConditions()

        onUserProfileClick()
    }, [
        isTermsAccepted,
        setIsTermsAccepted,
        acceptTermsAndConditions,
        onUserProfileClick,
    ])

    const logoOnly = React.useMemo(
        () =>
            location.pathname.includes('/bpm/contacts/details/') ? true : false,
        [location]
    )

    const upperChildren = React.useMemo(
        () => (
            <TopBarTools
                toggleSidebarOpen={toggleSidebarOpen}
                logoOnly={logoOnly}
            >
                {smScreen && responsiveMenus}
            </TopBarTools>
        ),
        [toggleSidebarOpen, smScreen, responsiveMenus, logoOnly]
    )

    const lowerChildren = React.useMemo(
        () => (
            <LowerToolbar
                customText={pageTitle}
                customTextColor={customTextColor}
                lowerBarBackgroundColor={barColor}
                customComponent={customComponent}
                routes={BPMRoutes}
                setHasLowerBottomToolbar={setHasLowerBottomToolbar}
                hasLowerBottomToolbar={hasLowerBottomToolbar}
                fullHeight={fullHeight}
                isCustomToolbar={isCustomToolbar}
            />
        ),
        [
            pageTitle,
            customTextColor,
            barColor,
            customComponent,
            setHasLowerBottomToolbar,
            hasLowerBottomToolbar,
            fullHeight,
            isCustomToolbar,
        ]
    )

    const designatedAdminRoutingProps: GetDesignatedAdminRoutingComponentProps | null = React.useMemo(() => {
        if (currentUserInfo && !firmDesignatedAdminLoading) {
            return {
                location: {
                    ...location,
                },
                firmId: currentUserInfo.account.accountId,
                kpUserId: currentUserInfo.kpUserId,
                getFirmDesignatedAdminUserId,
                firmDesignatedAdminCalled,
                firm: firmDesignatedAdminResponse?.firm,
            }
        }
        return null
    }, [
        firmDesignatedAdminResponse,
        currentUserInfo,
        firmDesignatedAdminCalled,
        getFirmDesignatedAdminUserId,
        location,
        firmDesignatedAdminLoading,
    ])

    const designatedAdminRoutingComponent = React.useMemo(() => {
        if (!designatedAdminRoutingProps) return null

        return getDesignatedAdminRoutingComponent({
            ...designatedAdminRoutingProps,
            callingFromAE: true,
        })
    }, [designatedAdminRoutingProps])

    useEffect(() => {
        if (
            location.pathname === '/bpm/account' &&
            !!currentUserInfo &&
            !firmDesignatedAdminCalled
        ) {
            getFirmDesignatedAdminUserId({
                variables: {
                    id: currentUserInfo?.account.accountId,
                },
            })
        }
    }, [
        location,
        currentUserInfo,
        getFirmDesignatedAdminUserId,
        firmDesignatedAdminCalled,
    ])

    useEffect(() => {
        if (!handleLoadingPageChange) return

        if (!client || !currentUserInfo || previewFeaturesLoading) {
            handleLoadingPageChange(true)
            return
        }

        // hide any open snackbar on page refresh
        if (history.action === 'POP' && !!hideGlobalSnackbar)
            hideGlobalSnackbar()

        if (!location.search.includes('newuser=1')) {
            handleLoadingPageChange(false)
            return
        }

        // if it includes admin as a role, redirect to /account and open the dialog
        if (currentUserInfo?.roles.includes(Admin) ?? false) {
            handleLoadingPageChange(!firmDesignatedAdminResponse)
            return
        }

        // if the user is not an admin, open the dialog where they get redirected
        handleLoadingPageChange(false)
    }, [
        client,
        currentUserInfo,
        previewFeaturesLoading,
        showLoadingScreenPage,
        firmDesignatedAdminResponse,
        onUserProfileClick,
        handleLoadingPageChange,
        hideGlobalSnackbar,
        location,
        history.action,
    ])

    useLayoutEffect(() => {
        contentContainerRef.current?.scrollTo(0, 0)
    }, [location.pathname, contentContainerRef])

    return showLoadingScreenPage ? null :
        !firmSupportContactLoading &&
            location.search.includes('newuser=1') &&
            currentUserInfo?.roles.includes(Admin) &&
            !!designatedAdminRoutingComponent ? (
        designatedAdminRoutingComponent
    ) : !!hideIndustryAnalytics &&
        location.pathname === '/bpm/analytics/legacy' ? (
        <Redirect
            to={{
                pathname: location.pathname,
                state: { forbidden: true },
            }}
        />
    ) : (
        <div
            style={{
                userSelect: 'none',
            }}
        >
            {isHarmonyHeader ? (
                <>
                    <ThemeProvider theme={harmonyHeaderTheme}>
                        <AppHeader
                            hideActionBar={true}
                            hideNavigation={true}
                            hamburgerOnClick={handleHamburgerClick}
                            hamburgerIsExpanded={sidebarOpen}
                            rightSideContent={harmonySideContent}
                        />
                    </ThemeProvider>
                    <div className={classes.harmonyLowerWrapper}>
                        {lowerChildren}
                    </div>
                </>
            ) : (
                <DoubleAppToolbar
                    UpperChildren={upperChildren}
                    LowerChildren={lowerChildren}
                />
            )}
            <Sidebar
                open={sidebarOpen}
                updateSidebarVisibility={toggleSidebarOpen}
                bottomContent={!smScreen && responsiveMenus}
                items={sidebarItems}
            />
            <ContentContainer
                top={isHarmonyHeader
                    ? (hasLowerBottomToolbar ? HARMONY_HEADER_HEIGHT_WITH_TOOLBAR : HARMONY_HEADER_HEIGHT)
                    : (hasLowerBottomToolbar ? CLASSIC_HEADER_HEIGHT_WITH_TOOLBAR : CLASSIC_HEADER_HEIGHT)
                }
                ref={contentContainerRef}
                className={location.pathname === ANALYTICS_HOME_PATH ? classes.analyticsContentContainer : undefined}
            >
                <MainPageRouting
                    routes={BPMRoutes}
                    userRoles={currentUserInfo?.roles ?? []}
                />
                {!userTandCDateLoading && !isTermsAccepted && (
                    <TermsAndConditions
                        isOpen={!isTermsAccepted}
                        handleDialogClose={handleTermsDialogDisplay}
                    ></TermsAndConditions>
                )}
                {!userTandCDateLoading && isTermsAccepted && (
                    <UserProfileUpdate
                        userProfileClicked={userProfileClicked}
                        handleDialogClose={handleDialogClose}
                    />
                )}
            </ContentContainer>
            {globalSnackbarPropsData && (
                <GlobalSnackbar
                    hideGlobalSnackbar={hideGlobalSnackbar}
                    {...globalSnackbarPropsData.globalSnackbarProps}
                />
            )}
        </div>
    )
}

export default MainPageBPM
